import { PLPSiteProduct } from "./types";

const STORAGE_PREFIX = "plp-loaded-pages";
// Prices and stock move, so a snapshot is only worth replaying for a while.
const SNAPSHOT_MAX_AGE_MS = 30 * 60 * 1000;

type LoadedPagesSnapshot = {
	page: number;
	baseCount: number;
	savedAt: number;
	products: PLPSiteProduct[];
};

// One snapshot per listing address. The lp value is left out of the key so a
// newer snapshot replaces the older one instead of piling up beside it.
function getStorageKey() {
	if (typeof window === "undefined") return null;

	const url = new URL(window.location.href);
	url.searchParams.delete("lp");
	return `${STORAGE_PREFIX}:${url.pathname}${url.search}`;
}

export function saveLoadedPages(page: number, baseCount: number, products: PLPSiteProduct[]) {
	const storageKey = getStorageKey();
	if (!storageKey || page <= 1 || !products.length) return;

	const snapshot: LoadedPagesSnapshot = { page, baseCount, savedAt: Date.now(), products };

	try {
		sessionStorage.setItem(storageKey, JSON.stringify(snapshot));
	} catch {
		// Storage can be full or unavailable; the pages just get fetched again.
	}
}

export function readLoadedPages(baseCount: number, expectedPage: number): LoadedPagesSnapshot | null {
	const storageKey = getStorageKey();
	if (!storageKey || expectedPage <= 1) return null;

	try {
		const rawValue = sessionStorage.getItem(storageKey);
		if (!rawValue) return null;

		const snapshot = JSON.parse(rawValue) as LoadedPagesSnapshot;
		if (!Array.isArray(snapshot.products) || !snapshot.products.length) return null;
		// The snapshot holds the pages the server did not send. It only fits a
		// render that starts from the same server payload and stops at the same
		// page — a direct ?lp=N entry already carries those pages, and its base
		// count says so.
		if (snapshot.baseCount !== baseCount || snapshot.page !== expectedPage) return null;

		if (Date.now() - snapshot.savedAt > SNAPSHOT_MAX_AGE_MS) {
			sessionStorage.removeItem(storageKey);
			return null;
		}

		return snapshot;
	} catch {
		return null;
	}
}
